// What the bytes of a Magic Studio image actually are — and the filename that
// matches them.
//
// A generation's format is chosen by the model, not by us: Nano Banana answers
// JPEG, other models answer PNG, and the same session can produce both. So
// every filename the studio hands out (the saved asset's name, the Download
// action) has to follow the bytes instead of a hard-coded ".png", otherwise the
// file lands in the Asset Lab named *.png with JPEG inside it and every
// downstream consumer (previews, thumbnailers, the extension badge) is misled.
//
// Detection is by magic number rather than by the declared mime type, because
// the declared type is only ever as good as the provider's own header.

const MIME_EXTENSION: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/avif': 'avif',
};

// Extensions this module is allowed to replace on an existing filename — kept
// in step with MIME_EXTENSION so renaming never eats an unrelated suffix.
const KNOWN_EXTENSION = /\.(png|jpe?g|webp|gif|avif)$/i;

// The file extension for an image mime type, or null when it isn't one we know
// (callers then leave the name alone rather than guess).
export function extensionForMime(mime: string | null | undefined): string | null {
  if (!mime) return null;
  return MIME_EXTENSION[mime.split(';')[0].trim().toLowerCase()] ?? null;
}

// The mime type a data URL declares ("data:image/jpeg;base64,…"). Null for
// blob:/http: URLs, where the bytes have to be read to know.
export function mimeFromDataUrl(url: string | null | undefined): string | null {
  if (!url || !url.startsWith('data:')) return null;
  const mime = url.slice(5, url.indexOf(',') >= 0 ? url.indexOf(',') : undefined).split(';')[0];
  return mime.startsWith('image/') ? mime.toLowerCase() : null;
}

// The image mime type of a byte buffer, read from its signature. Covers the
// formats a generation or an on-device tool can produce; null for anything else.
export function sniffImageMime(bytes: Uint8Array): string | null {
  if (bytes.length >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e) {
    return 'image/png';
  }
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg';
  }
  if (bytes.length >= 6 && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
    return 'image/gif';
  }
  // RIFF….WEBP — the format tag sits at offset 8, after the RIFF size field.
  if (
    bytes.length >= 12 &&
    String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3]) === 'RIFF' &&
    String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]) === 'WEBP'
  ) {
    return 'image/webp';
  }
  return null;
}

// The true mime type of a Blob: its signature first, its declared type as the
// fallback. Only the head of the blob is read, so this stays cheap on the
// multi-MB images the studio deals in.
export async function blobImageMime(blob: Blob): Promise<string | null> {
  try {
    const head = new Uint8Array(await blob.slice(0, 16).arrayBuffer());
    return sniffImageMime(head) ?? (blob.type || null);
  } catch {
    return blob.type || null;
  }
}

// Re-point a filename at the extension its bytes actually have. Unknown mime →
// the name is returned untouched (better a stale suffix than a wrong one).
export function withImageExtension(name: string, mime: string | null | undefined): string {
  const ext = extensionForMime(mime);
  if (!ext) return name;
  const base = name.replace(KNOWN_EXTENSION, '');
  return `${base}.${ext}`;
}
